'use client';

import { c as _c } from "react/compiler-runtime";
import './comms-preference-row.scss';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import type { CommsPreferenceEnum } from './comms-preference-row.logic.ts';
export type CommsOption = {
  id: string;
  label: string;
  value: string;
};
type CommsPreferenceRowBaseProps = {
  fieldName: string;
  title: string;
  description: string;
  options: CommsOption[];
  isDisabled?: boolean;
};
export const CommsPreferenceSection: React.FC<PropsWithChildren<{
  title?: string;
}>> = t0 => {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <h2 className="label-neutral-amplified label-potent label-xs py-comfy-lg">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="CommsPreference flex flex-col max-w-full">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const CommsPreferenceDivider = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="CommsPreference__divider" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const CommsPreferenceRow: React.FC<CommsPreferenceRowBaseProps> = t0 => {
  const $ = _c(25);
  const {
    fieldName,
    title,
    description,
    options,
    isDisabled: t1
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== title) {
    t2 = <h3 className="heading-xs heading-neutral-amplified">{title}</h3>;
    $[0] = title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== description) {
    t3 = <p className="body-sm body-neutral-mild">{description}</p>;
    $[2] = description;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className="flex flex-col gap-snug-sm">{t2}{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== title) {
    t5 = <legend className="sr-only">{title}</legend>;
    $[7] = title;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== fieldName || $[10] !== isDisabled || $[11] !== options) {
    let t7;
    if ($[13] !== fieldName || $[14] !== isDisabled) {
      t7 = option => <Checkbox key={option.id} name={`${fieldName}.${option.value}`} label={option.label} labelContent={<span className="label-moderate label-lg">{option.label}</span>} variant="tinted" disabled={isDisabled} />;
      $[13] = fieldName;
      $[14] = isDisabled;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t6 = options.map(t7);
    $[9] = fieldName;
    $[10] = isDisabled;
    $[11] = options;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <div className="flex flex-col gap-comfy-xs">{t6}</div>;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t7 || $[20] !== title) {
    t8 = <fieldset className="CommsPreference__options shrink-0" aria-label={title}>{t5}{t7}</fieldset>;
    $[18] = t5;
    $[19] = t7;
    $[20] = title;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t4 || $[23] !== t8) {
    t9 = <div className="CommsPreference__row flex flex-col gap-comfy-sm md:flex-row md:items-center md:justify-between py-comfy-lg">{t4}{t8}</div>;
    $[22] = t4;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};
type SectionProps = {
  options: CommsOption[];
  isDisabled?: boolean;
};
export const CommsPreferenceRowI18n: React.FC<SectionProps & {
  preference: CommsPreferenceEnum;
}> = t0 => {
  const $ = _c(15);
  let preference;
  let props;
  if ($[0] !== t0) {
    ({
      preference,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = preference;
    $[2] = props;
  } else {
    preference = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my-settings"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    t
  } = useTranslation(t1);
  const t2 = `my-settings:comms.${preference}.title`;
  let t3;
  if ($[4] !== t || $[5] !== t2) {
    t3 = t(t2);
    $[4] = t;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `my-settings:comms.${preference}.description`;
  let t5;
  if ($[7] !== t || $[8] !== t4) {
    t5 = t(t4);
    $[7] = t;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== preference || $[11] !== props || $[12] !== t3 || $[13] !== t5) {
    t6 = <CommsPreferenceRow fieldName={preference} title={t3} description={t5} {...props} />;
    $[10] = preference;
    $[11] = props;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};